import { ComponentProps, Fragment, useEffect } from 'react';
import cns from 'classnames';
import { ParseKeys } from 'i18next';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useViewportDevice } from '@zep/react';
import { Button } from '@zep/ui';
import { cn } from '@zep/utils';
import { usePrice } from '../../../../../services';
import { SubscribePlan } from '../../../../../types/SubscriptionType';
import { PlanTemplateType, useStartPlanPayment } from '../../../plan-payment';
import { usePricingHoverButton } from '../hooks/usePricingHoverButton';
import { usePricing } from '../PricingProvider';
import S from './PricingComparison.module.scss';
import { PricingComparisonRows } from './PricingComparisonRows';
import { StickyPlanSelect } from './StickyPlanSelect';
type PricingButton = {
  name: string;
  key: Exclude<SubscribePlan, 'free'>;
  buttonLabel: string;
  hasHoverEffect?: boolean;
};
const pricingButtonData: PricingButton[] = [{
  name: 'Basic',
  key: 'basic',
  buttonLabel: 'subscription:pricing.startButton',
  hasHoverEffect: true
}, {
  name: 'Pro',
  key: 'pro',
  buttonLabel: 'subscription:pricing.startButton'
}];
export const PricingComparison = () => {
  const router = useRouter();
  const {
    data
  } = usePrice({
    suspense: true
  });
  const {
    isMobile
  } = useViewportDevice();
  const {
    handleStartPlanPayment
  } = useStartPlanPayment();
  const {
    t
  } = useTranslation(['subscription'], {
    useSuspense: true
  });

  // @ts-ignore
  const {
    plans,
    visiblePlans,
    setVisiblePlans
  } = usePricing();
  const {
    handleMouseEnter,
    handleMouseLeave,
    hoverStates,
    pricingButtonStyle
  } = usePricingHoverButton();
  const handleChange: ComponentProps<typeof StickyPlanSelect>['onChange'] = (selected: SubscribePlan[]) => {
    setVisiblePlans(selected);
  };
  useEffect(() => {
    if (isMobile) {
      setVisiblePlans(plans.slice(0, 2));
    } else {
      setVisiblePlans(plans);
    }
  }, [isMobile, plans, setVisiblePlans]);
  const goUpdatePlan = (_plan: SubscribePlan) => {
    switch (_plan) {
      case 'free':
        {
          handleStartPlanPayment({
            planType: 'basic',
            planTemplateType: PlanTemplateType.PRODUCT_SUBSCRIPTION,
            subscriptionPeriod: '30'
          });
          break;
        }
      case 'basic':
        {
          handleStartPlanPayment({
            planType: 'pro',
            planTemplateType: PlanTemplateType.PRODUCT_SUBSCRIPTION,
            subscriptionPeriod: '30'
          });
          break;
        }
      case 'pro':
        {
          handleStartPlanPayment({
            planType: 'pro',
            planTemplateType: PlanTemplateType.PRODUCT_SUBSCRIPTION,
            subscriptionPeriod: '30'
          });
          break;
        }
    }
  };
  return <section className={S.section} data-sentry-component="PricingComparison" data-sentry-source-file="PricingComparison.tsx">
      <div className={S.inner}>
        <h2 className={S.title}>
          {t('subscription:pricing.comparison.title')}
        </h2>

        <StickyPlanSelect onChange={handleChange} onUpdateStickPlan={goUpdatePlan} data-sentry-element="StickyPlanSelect" data-sentry-source-file="PricingComparison.tsx" />

        <div className={S.table_container}>
          <table className={S.table}>
            <caption aria-hidden="true" hidden>
              {t('subscription:pricing.comparison.title')}
            </caption>

            <colgroup>
              <col />
              {visiblePlans.map(
            // @ts-ignore
            (plan, index) => <col key={index} className={cns(S.col, S[plan])} />)}
            </colgroup>

            <thead className={S.thead}>
              <tr>
                <th></th>
                <th scope="col">
                  <strong>Free</strong>
                  <Button size="xs" style={{
                  maxWidth: 110
                }} onClick={() => {
                  router.push('/home/spaces');
                }} className={S.button} isFullWidth variant={'sub'} data-sentry-element="Button" data-sentry-source-file="PricingComparison.tsx">
                    {t('subscription:pricing.comparison.buttonFree')}
                  </Button>
                </th>
                {pricingButtonData.map(plan => <th key={plan.key} scope="col">
                    <strong>{plan.name}</strong>
                    <Button size="xs" onClick={() => goUpdatePlan(plan.key)} className={S.button} isFullWidth onMouseEnter={() => handleMouseEnter(plan.key)} onMouseLeave={() => handleMouseLeave(plan.key)} style={{
                  backgroundColor: hoverStates[plan.key] ? pricingButtonStyle[plan.key].backgroundHoverColor : pricingButtonStyle[plan.key].backgroundColor,
                  maxWidth: 110
                }}>
                      {t(plan.buttonLabel as ParseKeys<'subscription'>)}
                    </Button>
                  </th>)}
                <th scope="col">
                  <strong>Enterprise</strong>
                  <Button size="xs" style={{
                  backgroundColor: hoverStates['enterprise'] ? pricingButtonStyle['enterprise'].backgroundHoverColor : pricingButtonStyle['enterprise'].backgroundColor,
                  maxWidth: 110
                }} onMouseEnter={() => handleMouseEnter('enterprise')} onMouseLeave={() => handleMouseLeave('enterprise')} onClick={() => {
                  //
                  window.open(t('subscription:subscription.salesmap.link'), '_blank');
                }} className={cn(S.button, '!text-white')} isFullWidth variant={'sub'} data-sentry-element="Button" data-sentry-source-file="PricingComparison.tsx">
                    {t('subscription:pricing.comparison.buttonContact')}
                  </Button>
                </th>
              </tr>
            </thead>

            <tbody className={S.tbody}>
              {data?.PricingComparison?.featureGroups?.map((group, index) => <Fragment key={index}>
                  <tr>
                    <th scope="row" className={S.tbody_title}>
                      {group.groupName}
                    </th>
                  </tr>

                  <PricingComparisonRows features={group.features} />
                </Fragment>)}
            </tbody>
          </table>
        </div>
      </div>
    </section>;
};